import type {
  AdCreativeItem,
  AdCreativeLocaleStatus,
  AdCreativeLocalizedMediaEntry,
} from '@segmently/schema-models/ad-creative';
import {
  canonicalLocaleMatchKey,
  resolveLocaleKey,
  resolveLocalizedValue,
  type LocaleResolutionMode,
  type LocaleResolutionReason,
} from '@modules/shared-locales';

export const CREATIVE_LOCALIZED_COPY_FIELDS = [
  'headline',
  'primaryText',
  'description',
  'cta',
  'overlayText',
  'subtitleScript',
] as const;

export type CreativeLocalizedCopyField = typeof CREATIVE_LOCALIZED_COPY_FIELDS[number];

export interface CreativeLocaleFieldProjection<T> {
  requestedLocale: string;
  resolvedLocale: string | null;
  value: T | undefined;
  /** True only when the requested locale itself owns a value. */
  hasRequestedValue: boolean;
  /** True when preview resolution selected a different stored locale. */
  usedFallback: boolean;
  reason: LocaleResolutionReason;
}

export interface CreativeLocaleProjection {
  requestedLocale: string;
  localeKey: string | null;
  sourceLocale: string;
  status: AdCreativeLocaleStatus | 'missing';
  isSource: boolean;
  copy: Record<CreativeLocalizedCopyField, CreativeLocaleFieldProjection<string>>;
  media: Record<string, CreativeLocaleFieldProjection<AdCreativeLocalizedMediaEntry>>;
  previewUrl: string | null;
  previewMediaKind: string | null;
  hasAnyFallback: boolean;
  missingCopyFields: CreativeLocalizedCopyField[];
}

export interface CreativeLocaleCoverage {
  active: number;
  ready: number;
  needsReview: number;
  missing: number;
  stale: number;
  failed: number;
  archived: number;
  readiness: 'ready' | 'review_required' | 'missing' | 'stale' | 'failed' | 'in_progress';
}

/**
 * Runtime ownership guard. Provider snapshots never enter the authored locale
 * editor, even when an untyped API payload accidentally reaches this surface.
 */
export function isSegmentlyOwnedCreativeItem(value: unknown): value is AdCreativeItem {
  if (!isRecord(value) || value.entityKind !== 'segmently_copy') return false;
  const defaultLanguage = nonEmptyString(value.defaultLanguage);
  if (!defaultLanguage || !nonEmptyString(value.sourceRevision)) return false;
  if (!Number.isInteger(value.localeRevision) || Number(value.localeRevision) < 1) return false;
  if (!Array.isArray(value.languages)) return false;
  const languages = value.languages
    .filter((entry): entry is string => typeof entry === 'string')
    .map((entry) => entry.trim())
    .filter(Boolean);
  if (languages.length === 0 || !languages.includes(defaultLanguage)) return false;
  if (!isRecord(value.statusByLocale)
    || value.statusByLocale[defaultLanguage] !== 'source'
    || !isRecord(value.localizedCopy)
    || !isRecord(value.localizedDestinations)
    || !Object.prototype.hasOwnProperty.call(value.localizedDestinations, defaultLanguage)) {
    return false;
  }
  const headline = value.localizedCopy.headline;
  const primaryText = value.localizedCopy.primaryText;
  return hasLocaleTranslation(headline, defaultLanguage)
    && hasLocaleTranslation(primaryText, defaultLanguage);
}

/** Resolve the exact active locale key without inventing or rewriting one. */
export function findCreativeLocaleKey(item: AdCreativeItem, locale: string): string | null {
  return resolveLocaleKey(locale, item.languages, { mode: 'strict' }).resolvedLocale;
}

export function getCreativeLocaleStatus(
  item: AdCreativeItem,
  locale: string,
): AdCreativeLocaleStatus | 'missing' {
  const direct = resolveLocaleKey(locale, Object.keys(item.statusByLocale ?? {}), { mode: 'strict' });
  return direct.resolvedLocale
    ? item.statusByLocale[direct.resolvedLocale] ?? 'missing'
    : 'missing';
}

/**
 * Build the locale-specific projection used by editor and library previews.
 * Missing and explicit blank/disabled are intentionally distinct: a stored
 * empty string or disabled media entry is returned as an owned value and never
 * triggers fallback.
 */
export function resolveCreativeLocaleProjection(
  item: AdCreativeItem,
  requestedLocale: string,
  mode: LocaleResolutionMode = 'preview',
): CreativeLocaleProjection {
  const localeKey = findCreativeLocaleKey(item, requestedLocale);
  const sourceLocale = findCreativeLocaleKey(item, item.defaultLanguage) ?? item.defaultLanguage;
  const effectiveRequestedLocale = localeKey ?? requestedLocale;
  const copy = {} as CreativeLocaleProjection['copy'];

  for (const field of CREATIVE_LOCALIZED_COPY_FIELDS) {
    const translations = item.localizedCopy?.[field]?.translations ?? {};
    copy[field] = projectLocalizedValue(
      translations,
      effectiveRequestedLocale,
      sourceLocale,
      mode,
    );
  }

  const media: CreativeLocaleProjection['media'] = {};
  for (const [slotKey, slot] of Object.entries(item.localizedMedia ?? {})) {
    media[slotKey] = projectLocalizedValue(
      slot.translations ?? {},
      effectiveRequestedLocale,
      sourceLocale,
      mode,
    );
  }

  const preview = selectPreviewMedia(item, media);
  const allValues = [...Object.values(copy), ...Object.values(media)];

  return {
    requestedLocale,
    localeKey,
    sourceLocale,
    status: getCreativeLocaleStatus(item, localeKey ?? requestedLocale),
    isSource: sameLocale(localeKey ?? requestedLocale, sourceLocale),
    copy,
    media,
    previewUrl: preview.url,
    previewMediaKind: preview.kind,
    hasAnyFallback: allValues.some((entry) => entry.usedFallback),
    missingCopyFields: CREATIVE_LOCALIZED_COPY_FIELDS.filter((field) => !copy[field].hasRequestedValue),
  };
}

/** Exact values for editing. Missing fields are omitted; explicit blanks stay. */
export function getExactCreativeLocaleCopy(
  item: AdCreativeItem,
  locale: string,
): Partial<Record<CreativeLocalizedCopyField, string>> {
  const copy: Partial<Record<CreativeLocalizedCopyField, string>> = {};
  for (const field of CREATIVE_LOCALIZED_COPY_FIELDS) {
    const projected = projectLocalizedValue(
      item.localizedCopy?.[field]?.translations ?? {},
      locale,
      item.defaultLanguage,
      'strict',
    );
    if (projected.hasRequestedValue) copy[field] = projected.value;
  }
  return copy;
}

export function creativeLocaleHeadline(
  item: AdCreativeItem,
  locale: string = item.defaultLanguage,
  mode: LocaleResolutionMode = 'preview',
): string {
  const field = resolveCreativeLocaleProjection(item, locale, mode).copy.headline;
  if (field.value !== undefined) return field.value;
  return item.templateId ?? item.id;
}

export function creativeLocaleSearchText(item: AdCreativeItem): string {
  const values: string[] = [];
  for (const field of CREATIVE_LOCALIZED_COPY_FIELDS) {
    values.push(...Object.values(item.localizedCopy?.[field]?.translations ?? {}));
  }
  return values.join(' ');
}

export function getCreativeLocaleCoverage(item: AdCreativeItem): CreativeLocaleCoverage {
  const statuses = item.languages.map((locale) => getCreativeLocaleStatus(item, locale));
  const ready = statuses.filter((status) => status === 'source' || status === 'ready').length;
  const needsReview = statuses.filter((status) => status === 'review_required' || status === 'draft' || status === 'partial').length;
  const missing = statuses.filter((status) => status === 'missing').length;
  const stale = statuses.filter((status) => status === 'stale').length;
  const failed = statuses.filter((status) => status === 'failed').length;
  const archived = Object.values(item.statusByLocale ?? {}).filter((status) => status === 'archived').length;

  let readiness: CreativeLocaleCoverage['readiness'] = 'in_progress';
  if (statuses.length > 0 && ready === statuses.length) readiness = 'ready';
  else if (failed > 0) readiness = 'failed';
  else if (stale > 0) readiness = 'stale';
  else if (missing > 0) readiness = 'missing';
  else if (needsReview > 0) readiness = 'review_required';

  return {
    active: statuses.length,
    ready,
    needsReview,
    missing,
    stale,
    failed,
    archived,
    readiness,
  };
}

function projectLocalizedValue<T>(
  translations: Readonly<Record<string, T>>,
  requestedLocale: string,
  defaultLocale: string,
  mode: LocaleResolutionMode,
): CreativeLocaleFieldProjection<T> {
  const exact = resolveLocalizedValue(translations, requestedLocale, { mode: 'strict' });
  const resolved = exact ?? resolveLocalizedValue(translations, requestedLocale, {
    defaultLocale,
    mode,
  });

  return {
    requestedLocale,
    resolvedLocale: resolved?.locale ?? null,
    value: resolved?.value,
    hasRequestedValue: Boolean(exact),
    usedFallback: Boolean(resolved && !exact),
    reason: resolved?.reason ?? 'missing',
  };
}

function selectPreviewMedia(
  item: AdCreativeItem,
  media: CreativeLocaleProjection['media'],
): { url: string | null; kind: string | null } {
  const priorities = ['image', 'poster', 'video'];
  const entries = Object.entries(media).sort(([left], [right]) => left.localeCompare(right));

  for (const kind of priorities) {
    for (const [slotKey, projected] of entries) {
      if (item.localizedMedia?.[slotKey]?.mediaKind !== kind) continue;
      const asset = projected.value?.asset;
      const url = asset?.original ?? asset?.small;
      if (url !== undefined && url !== '') return { url, kind };
    }
  }

  return { url: null, kind: null };
}

function isRecord(value: unknown): value is Record<string, any> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function nonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  return normalized || null;
}

function hasLocaleTranslation(value: unknown, locale: string): boolean {
  return isRecord(value)
    && isRecord(value.translations)
    && Object.prototype.hasOwnProperty.call(value.translations, locale);
}

function sameLocale(left: string, right: string): boolean {
  const leftKey = canonicalLocaleMatchKey(left);
  const rightKey = canonicalLocaleMatchKey(right);
  return Boolean(leftKey && rightKey && leftKey === rightKey);
}
